<template>
  <div class="w-full flex flex-col items-center justify-center min-h-[100px] py-24 mb-32">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <SparkSpinner />
    </div>
    <div v-else class="w-full relative h-64 pt-16 overflow-visible">
      <Bar ref="chartRef" :data="chartData" :options="chartOptions" />
      <div v-if="segments && segments.length" class="w-full flex flex-row mt-4 select-none">
        <template v-for="(segment, idx) in segments" :key="segment.label + idx">
          <div
            class="text-center font-sans text-13 text-gray-500"
            :style="{
              width: segmentLabelWidth(segment) + '%',
              minWidth: '40px',
              fontWeight: 500,
            }"
          >
            {{ segment.label }}
          </div>
        </template>
      </div>
      <div class="absolute top-0 right-0">
        <p class="text-primary-500 hover:cursor-pointer hover:text-primary-600" @click="downloadChart">Download</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';
import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import logoUrl from '@/assets/img/Logo_Positive_Transparent.png';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default {
  name: 'CarbonChartPart',
  components: { Bar, SparkSpinner, SparkButton },
  props: {
    segments: {
      type: Array,
      required: true, // [{ label, value, color, tooltip }]
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalValue: {
      type: [String, Number],
      default: '',
    },
    totalUnit: {
      type: String,
      default: '',
    },
    totalSegmentIndex: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    total() {
      return this.segments.reduce((sum, s) => sum + s.value, 0);
    },
    chartData() {
      return {
        labels: [''],
        datasets: this.segments.map((seg, i, arr) => {
          let borderRadius = { topLeft: 0, bottomLeft: 0, topRight: 0, bottomRight: 0 };

          // Single segment case - all corners rounded
          if (arr.length === 1) {
            borderRadius = { topLeft: 32, bottomLeft: 32, topRight: 32, bottomRight: 32 };
          }
          // First segment - left corners rounded
          else if (i === 0) {
            borderRadius = { topLeft: 32, bottomLeft: 32, topRight: 0, bottomRight: 0 };
          }
          // Last segment - right corners rounded
          else if (i === arr.length - 1) {
            borderRadius = { topLeft: 0, bottomLeft: 0, topRight: 32, bottomRight: 32 };
          }

          return {
            label: seg.label,
            data: [seg.value],
            backgroundColor: seg.color,
            borderRadius,
            borderSkipped: false,
          };
        }),
      };
    },
    chartOptions() {
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            backgroundColor: '#383e46',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#B8CE45',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              title: () => '',
              label: item => {
                const idx = item.datasetIndex;
                const labelLine = `${this.segments[idx]?.label}: ${item.raw} ${this.totalUnit}`;
                const tooltip = this.segments[idx]?.tooltip;
                if (tooltip && tooltip.lines && tooltip.lines.length) {
                  const customLines = tooltip.lines.map(l => `${l.label}\t${l.value}`);
                  return [labelLine, ...customLines];
                }
                // fallback
                return labelLine;
              },
            },
            padding: 12,
            caretSize: 8,
            cornerRadius: 8,
            bodySpacing: 6,
            titleFont: { family: 'Poppins', weight: 'bold', size: 14 },
            bodyFont: { family: 'Poppins', size: 13 },
          },
        },
        scales: {
          x: {
            stacked: true,
            display: false,
            max: this.total,
          },
          y: {
            stacked: true,
            display: false,
          },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 24,
            bottom: 0,
          },
        },
      };
    },
  },
  methods: {
    segmentLabelWidth(segment) {
      if (!this.segments.length) return 0;
      return (segment.value / this.total) * 100;
    },
    async downloadChart() {
      // Create a new canvas for the high-res chart
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d');
      const chartScale = 2; // Scale factor for the chart

      // Set the export canvas size
      exportCanvas.width = this.$refs.chartRef.$el.width * chartScale;
      exportCanvas.height = this.$refs.chartRef.$el.height * chartScale;
      exportCtx.scale(chartScale, chartScale);

      // Create a new chart instance for export
      const exportChart = new ChartJS(exportCtx, {
        type: 'bar',
        data: this.chartData,
        options: {
          ...this.chartOptions,
          animation: false,
          responsive: false,
          maintainAspectRatio: false,
        },
      });

      // Wait for the chart to render
      await new Promise(resolve => setTimeout(resolve, 100));

      const chartImage = new Image();
      chartImage.src = exportCanvas.toDataURL('image/png', 1.0);

      const logoImage = new Image();
      logoImage.src = logoUrl;

      await Promise.all([
        new Promise(resolve => (chartImage.onload = resolve)),
        new Promise(resolve => (logoImage.onload = resolve)),
      ]);

      // Create a new canvas for the composite image
      const compositeCanvas = document.createElement('canvas');
      const ctx = compositeCanvas.getContext('2d');

      // Set canvas size to accommodate chart, logo, and legend
      const padding = 20;
      const chartPadding = 40; // Additional padding for the chart
      const logoWidth = 100; // Set desired width first
      const logoHeight = (logoImage.height * logoWidth) / logoImage.width; // Calculate height based on aspect ratio
      const legendHeight = this.segments.length * 25 + padding;
      const baseWidth = this.$refs.chartRef.$el.width;
      const baseHeight = this.$refs.chartRef.$el.height + logoHeight + legendHeight + padding * 2;

      // Scale up the canvas for better quality
      const scale = 2; // Scale factor for higher quality
      compositeCanvas.width = baseWidth * scale;
      compositeCanvas.height = baseHeight * scale;
      ctx.scale(scale, scale);

      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, baseWidth, baseHeight);

      // Draw chart with padding
      const chartWidth = baseWidth - chartPadding * 2;
      const chartHeight = this.$refs.chartRef.$el.height - chartPadding * 2;

      // Enable image smoothing for better quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the chart at its original size
      ctx.drawImage(chartImage, chartPadding, padding + chartPadding, chartWidth, chartHeight);

      // Draw legend
      let legendY = this.$refs.chartRef.$el.height + padding * 2;

      // Define column positions for right-aligned values
      const labelColumnWidth = 200;
      const valueColumnX = padding + labelColumnWidth;

      // Draw total value row first
      ctx.fillStyle = '#383e46';
      ctx.font = 'bold 14px Poppins';
      const totalText = `Total ${this.totalValue} ${this.totalUnit}`;
      ctx.fillText('Total', padding, legendY);

      // Right-align total value
      ctx.fillStyle = '#383e46';
      const totalValueText = `${this.totalValue} ${this.totalUnit}`;
      const totalValueWidth = ctx.measureText(totalValueText).width;
      ctx.fillText(totalValueText, valueColumnX + 100 - totalValueWidth, legendY);

      // Add separator line
      legendY += 25;
      ctx.beginPath();
      ctx.strokeStyle = '#E5E7EB';
      ctx.moveTo(padding, legendY - 10);
      ctx.lineTo(valueColumnX + 100, legendY - 10); // Extend line beyond the value column
      ctx.stroke();

      // Draw segment rows
      this.segments.forEach((segment, index) => {
        // Draw color box
        ctx.fillStyle = segment.color;
        ctx.fillRect(padding, legendY + index * 25, 15, 15);

        // Crop label if too long and draw it
        ctx.fillStyle = '#383e46';
        ctx.font = '14px Poppins';
        const maxLabelWidth = labelColumnWidth - 35; // Account for color box and spacing
        let labelText = segment.label;
        let labelWidth = ctx.measureText(labelText).width;

        if (labelWidth > maxLabelWidth) {
          // Crop the text and add ellipsis
          while (labelWidth > maxLabelWidth - ctx.measureText('...').width && labelText.length > 0) {
            labelText = labelText.slice(0, -1);
            labelWidth = ctx.measureText(labelText).width;
          }
          labelText += '...';
        }

        ctx.fillText(labelText, padding + 25, legendY + index * 25 + 12);

        // Right-align value
        ctx.fillStyle = '#838A93';
        const valueText = `${segment.value} ${this.totalUnit}`;
        const valueWidth = ctx.measureText(valueText).width;
        ctx.fillText(valueText, valueColumnX + 100 - valueWidth, legendY + index * 25 + 12);
      });

      // Draw logo at the bottom right with text
      const logoY = baseHeight - logoHeight - padding;
      const text = 'Calculated with';
      ctx.font = '10px Poppins';
      ctx.fillStyle = '#838A93';
      const textWidth = ctx.measureText(text).width;
      const totalWidth = textWidth + 10 + logoWidth; // 10px spacing between text and logo
      const logoX = baseWidth - totalWidth - padding;

      // Draw text
      ctx.fillText(text, logoX, logoY + logoHeight / 2 + 5); // Center text vertically with logo

      // Draw logo
      ctx.drawImage(logoImage, logoX + textWidth + 10, logoY, logoWidth, logoHeight);

      // Create download link with high quality
      const link = document.createElement('a');
      link.download = 'spark-carbon-chart.png';
      link.href = compositeCanvas.toDataURL('image/png', 1.0); // Use maximum quality
      link.click();

      // Clean up
      exportChart.destroy();
    },
  },
};
</script>
